import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Thin top loading bar that animates on route changes.
 * Appears instantly on navigation, completes when the new route renders.
 */
const NavigationProgress = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    // Skip initial mount
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;

    // Start: show bar at 30% instantly
    setVisible(true);
    setProgress(30);

    // Advance to 70% over 200ms
    timerRef.current = setTimeout(() => setProgress(70), 80);

    // Complete to 100% after a tick (route has rendered by now)
    const complete = setTimeout(() => {
      setProgress(100);
      // Fade out after reaching 100%
      setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 200);
    }, 250);

    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(complete);
    };
  }, [location.pathname]);

  if (!visible && progress === 0) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
      data-testid="nav-progress-bar"
    >
      <div
        className="h-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.4)]"
        style={{
          width: `${progress}%`,
          transition: progress === 0
            ? 'none'
            : progress === 100
              ? 'width 150ms ease-out, opacity 200ms ease-out'
              : 'width 200ms ease-out',
          opacity: progress === 100 ? 0 : 1,
        }}
      />
    </div>
  );
};

export default NavigationProgress;
